/**
 * The clearance table: every station bin of a clearance run, at full
 * resolution, in the order the reader asked for — built to the same standard
 * as `StationVolumeTable`, which sits in the same dock slot on a volumes run.
 *
 * Shared with station volumes rather than re-invented: the click / shift-click
 * sort rules (`nextSort`), the Search STA parser and its bin-contains-station
 * test, the scroll-into-view arithmetic (`revealScrollTop`), the `--sv-*`
 * palette, the station formatting and the `VerdictBadge`. What is
 * clearance-shaped — the columns, the per-bin verdict, what a total of a
 * clearance column means — is in `@/lib/clearance/table`.
 *
 * **The body is windowed**, for the sibling table's reason: a run may have
 * 20 000 bins (`MAX_BINS` in `scripts/clearance.py`). The chrome, the 24 px
 * rows, the window, the hover-without-a-re-render and the walk row are
 * `DockTableFrame`'s, shared with the sibling so the two cannot drift apart.
 * Windowing is also why the rows share ONE tab stop that the arrow keys move —
 * a tab stop per row would skip whatever the window has not rendered.
 *
 * Row behaviour is the sibling's contract — click walks, drag brushes: a click
 * (or Enter / Space) only moves a running walk to the interval's centre and
 * clears any brush; only a drag on the chart sets the selection. The WALKED
 * row is the marked one (`aria-current="location"`), and rows outside a brush
 * are dimmed, exactly as in `StationVolumeTable`. A row click used to select
 * its bin, and a one-interval selection narrowed every KPI and hid the run
 * verdict. Escape clears a brush.
 */
import { useAtomValue, useSetAtom } from 'jotai';
import { Info } from 'lucide-react';
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import {
  DOCK_TABLE_BADGE,
  DOCK_TABLE_CELL,
  DOCK_TABLE_FOOT,
  DOCK_TABLE_ROW_PX,
  DOCK_TABLE_SHRINK,
  DockColumnsMenu,
  DockSortHeader,
  DockTableAction,
  DockTableEmptyRow,
  DockTableFilter,
  DockTableFrame,
  DockTablePad,
  DockTableSearch,
  useDockTableWindow,
  useFitColumnGroups,
} from '@/components/dock/DockTableFrame';
import { type Verdict, VerdictBadge } from '@/components/VerdictChip';
import {
  binAtStation,
  binVerdict,
  CLEARANCE_DEFAULT_SORT,
  clearanceTotals,
  type ClearanceSort,
  type ClearanceSortKey,
  type ClearanceTotals,
  filterClearanceBins,
  sortClearanceBins,
} from '@/lib/clearance/table';
import { DEFAULT_NEAR_MISS_BANDS_M } from '@/lib/clearance/chart';
import { clearanceBinsToTsv } from '@/lib/clearance/tsv';
import type { ClearanceBin, ClearanceJson } from '@/lib/clearance/types';
import { ruleVars } from '@/lib/clearance/useClearanceVerdict';
import { clearanceVerdict, marginalBandM } from '@/lib/clearance/verdict';
import { binCentreStation } from '@/lib/stationVolumes/aggregate';
import { formatFixed, formatSigned, formatStationRange, staOnly } from '@/lib/stationVolumes/format';
import { isDefaultSort, nextSort, parseStationQuery } from '@/lib/stationVolumes/tableModel';

export interface ClearanceTableProps {
  json: ClearanceJson;
  layerId: string;
  /** Where a row click walks, when the dock owns the walk (`ClearanceChart`'s
   *  prop of the same name). Omitted, a click moves a running walk on this
   *  run's alignment only. */
  onWalkTo?: (station: number) => void;
  /** The INTERNAL station the cursor stands at, when the dock owns it.
   *  Omitted, the table reads the walk itself. */
  cursorStation?: number | null;
}

/** Display stations keep 2 dp everywhere in the clearance dock (its range
 *  chip, the chart axis), so the table does too. */
const STA_DECIMALS = 2;

/** The bands the RUN used, in run order — never three literals. K2 fixes them
 *  at 0.1 / 0.2 / 0.35 m in v1 and says configurability comes later; a
 *  hardcoded `≤ 0.35 m` would start lying the day it does, silently, in five
 *  languages at once (M9). */
const NEAR_MISS_KEYS = ['near01', 'near02', 'near035'] as const;
type NearMissKey = (typeof NEAR_MISS_KEYS)[number];
const NEAR_MISS_FIELD: Record<NearMissKey, keyof ClearanceBin['nearMiss']> = {
  near01: 'within0_1M',
  near02: 'within0_2M',
  near035: 'within0_35M',
};
/** Fallback for a run whose params predate `nearMissBandsM` — the one copy in `chart.ts`. */
const DEFAULT_BANDS = DEFAULT_NEAR_MISS_BANDS_M;

/** One row's figures — a bin's, or a total's — so the body and the footer
 *  print through the same cell code and cannot format a column two ways.
 *  `verdict` is null on the "Shown" row: a verdict judges a bin or the whole
 *  run, never a filtered sub-range, so that cell stays empty. */
type Figures = Omit<ClearanceTotals, 'bins'> & { verdict: Verdict | null };

const binFigures = (b: ClearanceBin): Figures => ({
  points: b.pointCount,
  violations: b.violationCount,
  maxIntrusionM: b.maxIntrusionM,
  worstOffsetM: b.worstOffsetM,
  worstHeightM: b.worstHeightM,
  nearMiss: b.nearMiss,
  verdict: binVerdict(b),
});

/** An empty cell is an em dash, never a 0: "no violation in this bin" and
 *  "a violation of exactly zero depth" are different facts. */
const orDash = (v: number | null, fmt: (v: number) => string): string => (v === null ? '—' : fmt(v));

type FigureKey = Exclude<ClearanceSortKey, 'station'>;

/** `clearance-cell-<id>-<bin>` — `max` and `near01…` are the dock tests'
 *  long-standing contract; the rest follow the same shape. */
const BODY_TESTID: Record<FigureKey, string> = {
  verdict: 'verdict',
  points: 'points',
  violations: 'violations',
  maxIntrusion: 'max',
  worstOffset: 'offset',
  worstHeight: 'height',
  near01: 'near01',
  near02: 'near02',
  near035: 'near035',
};

/** Which width-fitted group a column belongs to (`useFitColumnGroups`). */
const COLUMN_GROUP: Partial<Record<ClearanceSortKey, 'position' | 'nearMiss'>> = {
  worstOffset: 'position',
  worstHeight: 'position',
  near01: 'nearMiss',
  near02: 'nearMiss',
  near035: 'nearMiss',
};

interface ColumnSpec {
  key: ClearanceSortKey;
  label: string;
  help: string;
  align: 'left' | 'center' | 'right';
}

export const ClearanceTable = ({ json, layerId, onWalkTo, cursorStation }: ClearanceTableProps) => {
  const { t } = useTranslation('project');
  const selection = useAtomValue(stationVolumeSelectionAtom);
  const setSelection = useSetAtom(stationVolumeSelectionAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const walk = useAtomValue(alignmentWalkAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);
  const [sorts, setSorts] = useState<ClearanceSort[]>(CLEARANCE_DEFAULT_SORT);
  const [query, setQuery] = useState('');
  const [onlySelection, setOnlySelection] = useState(false);
  const [onlyViolations, setOnlyViolations] = useState(false);
  // On by default, unlike the sibling's optional columns: these ARE the
  // clearance table's content, and the toggles exist to make room for it in
  // a narrow dock rather than to reveal extras. Null = automatic: shown while
  // the dock is wide enough (`useFitColumnGroups`), the near-miss ladder
  // giving way first, then the worst-point position. A reader's choice in
  // `Columns ▾` wins over the width either way.
  const [positionChoice, setPositionChoice] = useState<boolean | null>(null);
  const [nearMissChoice, setNearMissChoice] = useState<boolean | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fit = useFitColumnGroups(scrollRef, [
    { key: 'position', priority: 1, columns: 2, estPx: 230, explicit: positionChoice },
    { key: 'nearMiss', priority: 2, columns: 3, estPx: 240, explicit: nearMissChoice },
  ]);
  const showPosition = fit.visible.position;
  const showNearMiss = fit.visible.nearMiss;

  const sel = selection && selection.layerId === layerId ? selection : null;
  const selLo = sel ? Math.min(sel.fromBin, sel.toBin) : null;
  const selHi = sel ? Math.max(sel.fromBin, sel.toBin) : null;
  const bands = json.params.nearMissBandsM?.length ? json.params.nearMissBandsM : DEFAULT_BANDS;
  /** The band a bin turns MARGINAL on, for the verdict cells' tooltip. */
  const marginalBand = formatFixed(marginalBandM(bands), 2);

  const parsedQuery = useMemo(() => parseStationQuery(query), [query]);
  const queryUnparseable = query.trim().length > 0 && parsedQuery == null;

  // The brush bounds only enter the row list while "Only selection" is on —
  // otherwise a row click (which clears the brush) would hand the window a
  // new row order and scroll the table back to the top under the reader.
  const filterLo = onlySelection ? selLo : null;
  const filterHi = onlySelection ? selHi : null;
  const rows = useMemo(
    () =>
      sortClearanceBins(
        filterClearanceBins(json.bins, {
          station: parsedQuery,
          onlyViolations,
          selection: filterLo != null && filterHi != null ? { lo: filterLo, hi: filterHi } : null,
        }),
        sorts,
      ),
    [json.bins, parsedQuery, onlyViolations, filterLo, filterHi, sorts],
  );
  const filtered = rows.length !== json.bins.length;

  /** One scope per footer row, and each says which. The first sums exactly
   *  the rows on screen; the corridor row appears only once a filter makes
   *  the two differ. Folding a filtered subtotal and the corridor figure into
   *  one line is how a number gets quoted for the wrong extent. */
  const shownTotals = useMemo(() => clearanceTotals(rows), [rows]);
  const corridorTotals = useMemo(() => clearanceTotals(json.bins), [json.bins]);
  /** The corridor row carries THE run verdict — the one the dock header
   *  shows, from the summary's own `scoredPointCount` — not a re-derivation
   *  from the summed bins that could, one day, disagree with it. */
  const runVerdict = clearanceVerdict({
    violationCount: json.summary.violationCount,
    scoredCount: json.summary.scoredPointCount,
    nearMiss: json.summary.nearMiss,
  });

  /** The bin the cursor stands in: the dock's (walk or stand-in) when it
   *  passes one, else the walk's, if it is on this run's alignment. */
  const cursorAt =
    cursorStation !== undefined ? cursorStation : walk && walk.key === json.alignment.key ? walk.station : null;
  const walkBinIndex = useMemo(
    () => (cursorAt == null ? null : binAtStation(json.bins, cursorAt)),
    [cursorAt, json.bins],
  );

  const columns: ColumnSpec[] = [
    { key: 'station', label: t('clearance.table.station'), help: t('clearance.table.stationHelp'), align: 'left' },
    {
      key: 'verdict',
      label: t('clearance.table.verdict'),
      help: t('clearance.table.verdictHelp', ruleVars(marginalBand)),
      align: 'center',
    },
    { key: 'points', label: t('clearance.table.points'), help: t('clearance.table.pointsHelp'), align: 'right' },
    {
      key: 'violations',
      label: t('clearance.table.violations'),
      help: t('clearance.table.violationsHelp'),
      align: 'right',
    },
    {
      key: 'maxIntrusion',
      label: t('clearance.table.maxIntrusion'),
      help: t('clearance.table.maxIntrusionHelp'),
      align: 'right',
    },
    ...(showPosition
      ? [
          // The offset's help IS the sign convention: a `−2.00` is a violation
          // two metres to the LEFT (finding I5).
          {
            key: 'worstOffset' as const,
            label: t('clearance.table.worstOffset'),
            help: t('clearance.offsetConvention'),
            align: 'right' as const,
          },
          {
            key: 'worstHeight' as const,
            label: t('clearance.table.worstHeight'),
            help: t('clearance.table.worstHeightHelp'),
            align: 'right' as const,
          },
        ]
      : []),
    // Ruling K2: the near-miss ladder. Nested counts, so the unit is stated
    // once in the header and the three columns read as one widening band.
    ...(showNearMiss
      ? NEAR_MISS_KEYS.map((key, k) => ({
          key,
          label: t('clearance.table.nearMissBand', { value: formatFixed(bands[k] ?? DEFAULT_BANDS[k], 2) }),
          help: t('clearance.table.nearMissHelp'),
          align: 'right' as const,
        }))
      : []),
  ];
  const colCount = columns.length;

  // Keep the bin the rest of the dock is talking about on screen when it is
  // driven from elsewhere — the walk toolbar, the chart, the worst-point jump.
  // The walk wins when one is running; otherwise the start of the selection.
  // Revealed only when it MOVES: a sort or a filter never chases it.
  const win = useDockTableWindow({
    rows,
    keyOf: (b: ClearanceBin) => b.i,
    revealKey: walkBinIndex ?? selLo,
    measureKey: `${colCount}:${filtered}`,
    scrollRef,
  });

  // ----------------------------------------------------------- interaction
  /** A row click walks, on exactly the terms the chart's bars do: the cursor
   *  to the bin's centre, `follow` untouched, and nothing at all unless a walk
   *  is already running on this run's alignment. It also drops this run's
   *  brush — a click is a "go there", and a stale range would keep every KPI
   *  narrowed to somewhere the reader has left. */
  const walkTo = (b: ClearanceBin) => {
    const station = binCentreStation(b);
    if (onWalkTo) onWalkTo(station);
    else setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
    if (sel) setSelection(null);
    win.setFocusedKey(b.i);
  };

  /** The rows on screen, filtered and in the reader's order, in the worker
   *  CSV's columns. Clipboard access can be refused, absent or throw — say so
   *  rather than leaving the reader to discover an empty paste. */
  const copyShown = () => {
    if (rows.length === 0) return;
    const tsv = clearanceBinsToTsv(rows);
    const copied = () =>
      toast.success(t('clearance.table.copied', { count: rows.length, formatted: rows.length.toLocaleString() }));
    const failed = () => toast.error(t('clearance.table.copyFailed'));
    try {
      const written = navigator.clipboard?.writeText(tsv);
      if (!written) {
        failed();
        return;
      }
      written.then(copied, failed);
    } catch {
      failed();
    }
  };

  const onRowKeyDown = (e: ReactKeyboardEvent<HTMLTableRowElement>, b: ClearanceBin) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      walkTo(b);
      return;
    }
    if (e.key === 'Escape' && sel) {
      e.preventDefault();
      setSelection(null);
      return;
    }
    const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    win.moveFocus(b.i, step);
  };

  const sortIndex = (key: ClearanceSortKey) => sorts.findIndex((s) => s.key === key);

  // The tab stop follows the last row arrowed to, then the walk cursor, then
  // the selection, then the top — so tabbing out and back returns you there.
  const rovingBin = win.rovingKey(walkBinIndex, selLo);

  // ----------------------------------------------------------------- cells
  /** The house badge (`VerdictBadge`, as on the dock header's chip), shrunk
   *  to the row's density. A pill, not a filled cell: a filled cell turned a
   *  failing run's column into one solid red stripe, and fought the walk tint
   *  on the walked row. The label is printed, so it needs no sr-only twin. */
  const verdictContent = (v: Verdict): ReactNode => <VerdictBadge verdict={v} className={DOCK_TABLE_BADGE} />;

  const figureText = (key: FigureKey, f: Figures): string => {
    switch (key) {
      case 'verdict':
        return '';
      case 'points':
        return f.points.toLocaleString();
      case 'violations':
        return f.violations.toLocaleString();
      case 'maxIntrusion':
        return orDash(f.maxIntrusionM, (v) => formatFixed(v, 3));
      // Signed with an explicit `+`: the column holds both sides of the
      // centreline, and a bare `1.10` beside a `−1.10` hides which is which.
      case 'worstOffset':
        return orDash(f.worstOffsetM, (v) => formatSigned(v, 2));
      case 'worstHeight':
        return orDash(f.worstHeightM, (v) => formatFixed(v, 2));
      default:
        return f.nearMiss[NEAR_MISS_FIELD[key]].toLocaleString();
    }
  };

  /** The figure cells of one row, after its station cell. `testid` names a
   *  cell by column — the body's `clearance-cell-max-<i>` contract. */
  const figureCells = (f: Figures, testid: (key: FigureKey) => string) =>
    columns
      .filter((c): c is ColumnSpec & { key: FigureKey } => c.key !== 'station')
      .map((c) =>
        c.key === 'verdict' ? (
          <td
            key={c.key}
            data-testid={testid(c.key)}
            data-verdict={f.verdict}
            title={f.verdict ? t(`clearance.table.verdictCellHelp.${f.verdict}`, ruleVars(marginalBand)) : undefined}
            // The column shrinks to its badge instead of taking a share of
            // the table's slack.
            className={`text-center ${DOCK_TABLE_CELL} ${DOCK_TABLE_SHRINK}`}
          >
            {f.verdict && verdictContent(f.verdict)}
          </td>
        ) : (
          <td key={c.key} data-testid={testid(c.key)} className={DOCK_TABLE_CELL}>
            {figureText(c.key, f)}
          </td>
        ),
      );

  const footerRow = (scope: 'total' | 'corridor', totals: ClearanceTotals, verdict: Verdict | null, label: string) => (
    <tr data-testid={`clearance-${scope}-row`}>
      <th scope='row' data-testid={`clearance-${scope}-label`} className={`text-left font-semibold ${DOCK_TABLE_CELL}`}>
        {label}
      </th>
      {figureCells({ ...totals, verdict }, (key) => `clearance-${scope}-${key}`)}
    </tr>
  );

  const emptyText =
    parsedQuery != null && !onlyViolations && !onlySelection
      ? t('stationVolumes.table.noMatch', { station: staOnly(parsedQuery, STA_DECIMALS) })
      : t('clearance.table.noMatchFilters');

  const offsetConvention = t('clearance.offsetConvention');

  return (
    <DockTableFrame
      scrollRef={win.scrollRef}
      onScroll={win.onScroll}
      scrollTestId='clearance-table-scroll'
      // The interaction hint as the scroll area's tooltip rather than a
      // permanent line: the dock is narrow, and the contract is the sibling's,
      // which a reader learns once.
      scrollTitle={t('clearance.table.hint')}
      caption={t('clearance.table.caption')}
      toolbar={
        <>
          <DockTableSearch
            label={t('stationVolumes.table.searchSta')}
            value={query}
            onChange={setQuery}
            placeholder={t('stationVolumes.table.searchStaPlaceholder')}
            clearLabel={t('stationVolumes.table.searchStaClear')}
            invalid={queryUnparseable}
            errorText={t('stationVolumes.errors.stationUnparseable')}
            testId='clearance-table-search'
            clearTestId='clearance-table-search-clear'
          />
          <DockTableFilter
            testId='clearance-table-only-violations'
            label={t('clearance.table.onlyViolations')}
            checked={onlyViolations}
            onCheckedChange={setOnlyViolations}
          />
          {sel && (
            <DockTableFilter
              testId='clearance-table-only-selection'
              label={t('stationVolumes.table.onlySelection')}
              checked={onlySelection}
              onCheckedChange={setOnlySelection}
            />
          )}
        </>
      }
      columns={
        <DockColumnsMenu
          testId='clearance-table-columns'
          options={[
            {
              key: 'position',
              label: t('clearance.table.colPosition'),
              title: offsetConvention,
              checked: showPosition,
              onCheckedChange: setPositionChoice,
              autoHidden: fit.autoHidden('position'),
              testId: 'clearance-table-col-position',
            },
            {
              key: 'near-miss',
              label: t('clearance.table.colNearMiss'),
              title: t('clearance.table.nearMissHelp'),
              checked: showNearMiss,
              onCheckedChange: setNearMissChoice,
              autoHidden: fit.autoHidden('nearMiss'),
              testId: 'clearance-table-col-near-miss',
            },
          ]}
          hiddenCount={fit.hiddenColumns}
        />
      }
      actions={
        <>
          {!isDefaultSort(sorts) && (
            <DockTableAction testId='clearance-table-reset-order' onClick={() => setSorts(CLEARANCE_DEFAULT_SORT)}>
              {t('stationVolumes.table.resetOrder')}
            </DockTableAction>
          )}
          {/* The sibling dock's "Copy as TSV" is selection-aware; this one is
              view-aware — what the filters and sort put on screen, in that
              order — so a reader can paste exactly the rows they are reading. */}
          <DockTableAction
            outlined
            testId='clearance-table-copy'
            title={t('clearance.table.copyShownHelp')}
            disabled={rows.length === 0}
            onClick={copyShown}
          >
            {t('clearance.table.copyShown')}
          </DockTableAction>
        </>
      }
    >
      <thead>
        <tr>
          {columns.map((col) => {
            const at = sortIndex(col.key);
            return (
              <DockSortHeader
                key={col.key}
                testId={`clearance-th-${col.key}`}
                label={col.label}
                help={col.help}
                sortAt={at}
                dir={at >= 0 ? sorts[at].dir : null}
                onSort={(shift) => setSorts((current) => nextSort(current, col.key, shift))}
                align={col.align}
                shrink={col.key === 'station' || col.key === 'verdict'}
                group={COLUMN_GROUP[col.key]}
                extra={
                  // Finding I5. The offset column is the single most
                  // confusable quantity in the feature — a `−2.00` is a
                  // violation two metres to the LEFT. The convention used
                  // to be a full sentence row above the table, which cost
                  // a 280 px dock a row of data; it is now this ⓘ on the
                  // header it explains, its tooltip and its accessible
                  // text the same sentence.
                  col.key === 'worstOffset' ? (
                    <span
                      data-testid='clearance-offset-convention'
                      title={offsetConvention}
                      className='inline-flex cursor-help text-slate-400 dark:text-stone-500'
                    >
                      <Info aria-hidden className='size-3' />
                      <span className='sr-only'>{offsetConvention}</span>
                    </span>
                  ) : undefined
                }
              />
            );
          })}
        </tr>
      </thead>

      <tbody ref={win.bodyRef}>
        {rows.length === 0 && (
          <DockTableEmptyRow colSpan={colCount} testId='clearance-table-empty'>
            {emptyText}
          </DockTableEmptyRow>
        )}
        <DockTablePad height={win.padTopPx} colSpan={colCount} testId='clearance-pad-top' />
        {win.windowRows.map((b) => {
          const inBrush = selLo != null && selHi != null && b.i >= selLo && b.i <= selHi;
          return (
            <tr
              key={b.i}
              data-bin={b.i}
              data-testid={`clearance-row-${b.i}`}
              // Walking is what the cross-section and the 3D cursor hang
              // off, so it cannot be mouse-only (M7). Focus is an OUTLINE:
              // a ring is a box-shadow, which a `border-collapse` row does
              // not paint.
              tabIndex={rovingBin === b.i ? 0 : -1}
              aria-current={walkBinIndex === b.i ? 'location' : undefined}
              data-in-brush={inBrush || undefined}
              className='sv-row cursor-pointer border-b border-[var(--sv-grid)] focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-500'
              style={{
                height: DOCK_TABLE_ROW_PX,
                // The sibling's 55 % outside the brush: still readable,
                // plainly not the range the dock's KPIs are talking about.
                opacity: selLo == null || inBrush ? undefined : 0.55,
              }}
              onPointerEnter={() => setHover(b.i)}
              onPointerLeave={() => setHover(null)}
              onFocus={() => {
                setHover(b.i);
                win.setFocusedKey(b.i);
              }}
              onBlur={() => setHover(null)}
              onKeyDown={(e) => onRowKeyDown(e, b)}
              onClick={() => walkTo(b)}
            >
              <th scope='row' className={`sv-sta text-left font-normal ${DOCK_TABLE_CELL} ${DOCK_TABLE_SHRINK}`}>
                {formatStationRange(b, STA_DECIMALS)}
              </th>
              {figureCells(binFigures(b), (key) => `clearance-cell-${BODY_TESTID[key]}-${b.i}`)}
            </tr>
          );
        })}
        <DockTablePad height={win.padBottomPx} colSpan={colCount} testId='clearance-pad-bottom' />
      </tbody>

      {/* The whole band is sticky, so a second (corridor) row stacks under
          the first rather than needing its own offset. */}
      <tfoot className={DOCK_TABLE_FOOT}>
        {filtered &&
          footerRow(
            'total',
            shownTotals,
            null,
            t('clearance.table.totalsShown', {
              count: json.bins.length,
              formatted: json.bins.length.toLocaleString(),
              shown: rows.length.toLocaleString(),
            }),
          )}
        {footerRow(
          'corridor',
          corridorTotals,
          runVerdict,
          t('clearance.table.totalsCorridor', {
            count: json.bins.length,
            formatted: json.bins.length.toLocaleString(),
          }),
        )}
      </tfoot>
    </DockTableFrame>
  );
};
